import { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet';
import { useHistory } from 'react-router-dom';
import { FormattedMessage, useIntl } from 'react-intl';
import moment from 'moment';
import { Button, Divider, Modal, Popover, Space, Tooltip } from 'antd';

import {
  CustomerReduced,
  ListCustomersQuery,
} from '@/core/domain/people/Customers/Customers.types';
import {
  useCustomerReducedList,
  useDeleteCustomer,
} from '@/core/domain/people/Customers/Customers.hooks';

import { applyMask, removeMask } from '@/utils/mask';
import { cnpjMask, cpfMask } from '@/utils/mask/commonMasks';
import { flatInfiniteData } from '@/utils/request/flatInfiniteData';

import { PageProps } from '@/types/PageProps';

import AppPageHeader from '@/components/_common/AppPageHeader';
import InfiniteTable, { ColumnsType } from '@/components/_common/InfiniteTable';
import CustomersExtraFilters from '@/components/Customers/CustomersExtraFilters';
import { getTableScrollX } from '@/utils/table';
import CustomersReportModal from '../../../components/Customers/CustomersReportModal/index';
import DeleteIcon from '@/components/_common/DeleteIcon';
import EditIcon from '@/components/_common/EditIcon';
import SpaceForButton from '@/components/_common/SpaceForButtons';
import { Gift } from 'react-feather';
import { useDebounce } from '@/hooks';
import Authorities from '@/types/Authority';
import { convertParams, usePersistSearchParams } from '@/utils/queryParams';
import { DefaultRouteTabs, Group } from '@/components/_common';
import { hasPermissions } from '@/utils/functions/hasPermissions';
import usePageSearchHistory from '@/hooks/pageSearchHistory';
import CustomerCategoryTag from '@/components/_common/CustomerCategoryTag';

function CustomerListPage({ route }: PageProps) {
  const intl = useIntl();
  const history = useHistory();
  const [queryParams, setQueryParams] = useState<ListCustomersQuery>({
    order: 'ASC',
    active: true,
    ...convertParams<ListCustomersQuery>({
      active: 'boolean',
      cpfCnpj: 'string',
      fantasyName: 'string',
      genderType: 'string',
      licensePlate: 'string',
      personType: 'string',
      endBirthDate: 'string',
      search: 'string',
      startBirthDate: 'string',
      contact: 'string',
    }),
  });
  const [modalReportVisible, setModalReportVisible] = useState(false);

  function toggleReportModal() {
    setModalReportVisible(!modalReportVisible);
  }

  usePersistSearchParams(queryParams);
  const queryDebounce = useDebounce(queryParams);

  const { setLocation } = usePageSearchHistory();
  useEffect(setLocation, [queryDebounce]);

  const { data, isFetching, fetchNextPage, hasNextPage, refetch } =
    useCustomerReducedList(
      {
        ...queryDebounce,
        contact:
          !!queryDebounce?.contact && /\(|\)/.test(queryDebounce?.contact)
            ? removeMask(queryDebounce?.contact)
            : queryDebounce?.contact,
      },
      hasPermissions([Authorities.REGISTER_CUSTOMER_VIEW]),
    );

  const customers = flatInfiniteData(data);

  const removeMutation = useDeleteCustomer();

  const handleRemoveClick = (customer: CustomerReduced) => {
    Modal.confirm({
      title: intl.formatMessage({
        id: 'customers.notifications.remove.confirm.title',
      }),
      content: `${intl.formatMessage({
        id: 'customers.notifications.remove.confirm.content',
      })} ${customer?.name}`,
      onOk: async () => {
        await removeMutation.mutateAsync(customer.id);

        refetch();
      },
      okType: 'danger',
    });
  };

  const columns: ColumnsType<CustomerReduced> = [
    {
      dataIndex: 'category',
      title: 'Categoria',
      width: 100,
      align: 'center',
      render: (_, row) =>
        !!row.category?.id && (
          <Group position="center">
            <Tooltip
              title={row.category.name.toUpperCase()}
              color={row.category.color}
              placement="right"
            >
              <CustomerCategoryTag
                color={row.category.color}
                label={row.category.name}
              />
            </Tooltip>
          </Group>
        ),
    },
    {
      dataIndex: 'name',
      title: <FormattedMessage id="customers.list.header.name" />,
      ellipsis: true,
      width: 220,
    },
    {
      dataIndex: 'cpfCnpj',
      title: <FormattedMessage id="customers.list.header.cpfCnpj" />,
      render: (value: string) => {
        if (!value) {
          return '-';
        }
        if (value?.length === 11) {
          return applyMask(value, cpfMask);
        }
        if (value?.length === 14) {
          return applyMask(value, cnpjMask);
        }
        return value;
      },
      ellipsis: true,
      width: 220,
    },
    {
      dataIndex: 'foreignDocument',
      title: 'Documento estrangeiro',
      ellipsis: true,
      width: 220,
      render: (value: string) => {
        if (!value) {
          return '-';
        }

        return value;
      },
    },
    {
      dataIndex: 'creation',
      title: <FormattedMessage id="customers.list.header.includedAt" />,
      render: (text) => moment(text).format('DD/MM/YYYY'),
      ellipsis: true,
      width: 140,
    },
    {
      dataIndex: 'actions',
      title: <FormattedMessage id="customers.list.header.actions" />,
      render: (_, record) => (
        <Space>
          {moment().format('MM-DD') === record.birthDate?.substring(5) && (
            <>
              <Popover content="É aniversário deste cliente">
                <Gift size={20} />
              </Popover>
              <Divider type="vertical" />
            </>
          )}

          <EditIcon
            redirect={`/app/customers/${record.id}/general`}
            disabled={!hasPermissions([Authorities.REGISTER_CUSTOMER_EDIT])}
          />

          <Divider type="vertical" />

          <DeleteIcon
            onClick={() => handleRemoveClick(record)}
            disabled={!hasPermissions([Authorities.REGISTER_CUSTOMER_DELETE])}
          />
        </Space>
      ),
      ellipsis: true,
      width: 100,
      align: 'right',
      fixed: 'right',
    },
  ];

  const filterRoutes = route.parent.routes.filter(
    ({ key }) => key === 'indicators' || key === 'list',
  );
  route.parent.routes = filterRoutes;

  return (
    <>
      <Helmet title={intl.formatMessage({ id: route.name })} />

      <DefaultRouteTabs
        permissions={route.permissions}
        route={route.parent}
        baseKey={route.parent.key}
        basePath={route.parent.path}
        extra={
          <SpaceForButton>
            <Button
              block
              onClick={() => history.replace('/privateReports/customerTags')}
              disabled={
                !hasPermissions([Authorities.REGISTER_CUSTOMER_REPORTS])
              }
            >
              Gerar Etiquetas
            </Button>

            <Button
              block
              type="primary"
              onClick={() => toggleReportModal()}
              disabled={
                !hasPermissions([Authorities.REGISTER_CUSTOMER_REPORTS])
              }
            >
              <FormattedMessage id="suppliers.searchBar.generateReport" />
            </Button>

            <Button
              block
              type="primary"
              onClick={() => history.push('/app/customers/create')}
              disabled={!hasPermissions([Authorities.REGISTER_CUSTOMER_ADD])}
            >
              <FormattedMessage id="customers.list.newCustomer" />
            </Button>
          </SpaceForButton>
        }
      >
        <AppPageHeader
          searchValue={queryParams?.search}
          onSearch={(value) =>
            setQueryParams((prev) => ({ ...prev, search: value }))
          }
          headerActions={({ isShowingFilters }) =>
            isShowingFilters && (
              <Button
                type="primary"
                onClick={() =>
                  setQueryParams({
                    order: 'ASC',
                    active: true,
                  })
                }
              >
                {intl.formatMessage({ id: 'commons.clear.filters' })}
              </Button>
            )
          }
          extraFilters={
            <CustomersExtraFilters
              queryParams={queryParams}
              setQueryParams={setQueryParams}
            />
          }
        />

        <InfiniteTable<CustomerReduced>
          size="small"
          columns={columns}
          dataSource={customers}
          loading={isFetching}
          rowKey={(row) => row.id}
          hasNextPage={hasNextPage}
          fetchNextPage={fetchNextPage}
          scroll={{ x: getTableScrollX(columns) }}
          rowChange={(row) =>
            setQueryParams((preview) => ({ ...preview, rows: row }))
          }
        />

        <CustomersReportModal
          visible={modalReportVisible}
          handleVisible={() => toggleReportModal()}
        />
      </DefaultRouteTabs>
    </>
  );
}
export default CustomerListPage;
